"use client";

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Archive, ArchiveRestore, Bot, CheckCircle2, Clock3, Code2, GitBranch,
  Grid2X2, List, Mic2, MoreVertical, Plus, Search, Shield, Sparkles, Star,
  Timer, Video,
} from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select } from "@/components/ui/select";
import { Card, MetricCard, PageHeader, Pill } from "@/components/pulse-shell";
import { AgentEditModal, type AgentEditSectionId } from "@/components/agent-edit-modal";
import { AgentEditConversationPanel } from "@/components/agent-edit-conversation";
import { AgentEditEndOfCallPanel } from "@/components/agent-edit-end-of-call";
import { AgentEditGuardsPanel } from "@/components/agent-edit-guards";
import { AgentEditEndpointingPanel } from "@/components/agent-edit-endpointing";
import { AgentEditVoicePanel } from "@/components/agent-edit-voice";
import { AgentEditDirectVoicePanel } from "@/components/agent-edit-direct-voice";
import { AgentEditLiveAvatarVoicePanel } from "@/components/agent-edit-liveavatar-voice";
import { AgentEditCallControlPanel } from "@/components/agent-edit-call-control";
import { AgentEditSessionLimitsPanel } from "@/components/agent-edit-session-limits";
import { AgentEditPerceptionPanel } from "@/components/agent-edit-perception";
import { AgentEditVideoAvatarPanel } from "@/components/agent-edit-video-avatar";
import {
  api,
  DEFAULT_AGENT_PROMPTS,
  promptsForAgent,
  type Agent,
  type AgentVersion,
  type Settings,
} from "@/lib/api";
import { useAuthProfile, useRoleImpersonation } from "@/lib/auth-context";
import { CHECKUP_PROTOCOLS } from "@/lib/checkup-protocols";
import { filterAgentsByAllowlist } from "@/lib/session-role-scope";
import { hasPermission } from "@/lib/users-rbac";
import { diffSideBySide, hasDiff, type DiffLine } from "@/lib/text-diff";
import { clampTtsSpeed, clampTtsVolume } from "@/lib/voice-catalog";
import {
  DEFAULT_AGENT_MODE,
  DIRECT_MODEL_DEFAULT,
  DIRECT_VOICE_DEFAULT,
  agentModeLabel,
  normalizeAgentMode,
  type AgentMode,
} from "@/lib/agent-mode";
import { alternateAgentAvatarUrl, resolveAgentAvatarUrl } from "@/lib/agent-media";

const PULSE_ORIGIN = "https://pulse.igentify.in";

function AgentAvatarImg({
  src,
  avatarKey,
  alt = "",
}: {
  src: string;
  avatarKey?: string | null;
  alt?: string;
}) {
  return (
    <img
      src={src}
      alt={alt}
      onError={(e) => {
        const el = e.currentTarget;
        if (el.dataset.fallbackTried) return;
        const next = alternateAgentAvatarUrl(el.src, avatarKey);
        if (!next) return;
        el.dataset.fallbackTried = "1";
        el.src = next;
      }}
    />
  );
}

const PERSONA_META: Record<string, { role: string; desc: string; color: string }> = {
  nova: { role: "Customer Success", desc: "Retention, account guidance, and customer health conversations.", color: "#176fff" },
  avery: { role: "Sales Guide", desc: "Discovery, qualification, product matching, and follow-up.", color: "#8057e8" },
  orion: { role: "Onboarding", desc: "Implementation planning, migration, and first-value coaching.", color: "#0aa878" },
  mika: { role: "Medical Assistant", desc: "Careful medical information support — never diagnoses or prescribes.", color: "#ec8a34" },
  Emma: { role: "Doctor", desc: "Telehealth clinician — short visits, red-flag screen, monitoring plans; not emergency care.", color: "#0d9488" },
  maya: {
    role: "F1CDx Report Guide",
    desc: "Educational FoundationOne CDx report guide for patient demos — general education only, not medical advice.",
    color: "#0d9488",
  },
  mia: {
    role: "Diabetes Education Assistant",
    desc: "Plain-language diabetes education (tests, blood sugar, healthy living). General education only, not medical advice.",
    color: "#0d9488",
  },
  brian: {
    role: "Diabetes Education Assistant",
    desc: "Legacy id — use agent mia in customer links.",
    color: "#0d9488",
  },
  lynn: {
    role: "Diabetes Education Assistant",
    desc: "Legacy id — use agent mia in customer links.",
    color: "#0d9488",
  },
  "pulse-default": {
    role: "Pulse Expert",
    desc: "Igentify Pulse product guide — sessions, Admin, agents, and best practices.",
    color: "#176fff",
  },
};

/** Seed order for the library; ids match Dynamo agentId (pulse-default displays as Kira). */
const PERSONA_SEED: Array<{ agentId: string; fallbackName: string }> = [
  { agentId: "nova", fallbackName: "Nova" },
  { agentId: "avery", fallbackName: "Avery" },
  { agentId: "orion", fallbackName: "Orion" },
  { agentId: "mika", fallbackName: "Mika" },
  { agentId: "Emma", fallbackName: "Emma" },
  { agentId: "maya", fallbackName: "Maya" },
  { agentId: "pulse-default", fallbackName: "Kira" },
];

const COMPARE_FIELDS: Array<{ key: keyof Agent; label: string }> = [
  { key: "agentMode", label: "Agent mode" },
  { key: "directModel", label: "Direct model" },
  { key: "directVoice", label: "Direct voice" },
  { key: "elevenLabsVoiceId", label: "ElevenLabs voice" },
  { key: "liveAvatarVoiceId", label: "LiveAvatar voice id" },
  { key: "liveAvatarContextId", label: "LiveAvatar context id" },
  { key: "liveAvatarPrompt", label: "LiveAvatar prompt" },
  { key: "liveAvatarLinks", label: "LiveAvatar links" },
  { key: "greetingText", label: "Greeting" },
  { key: "systemPrompt", label: "System prompt" },
  { key: "knowledgeBase", label: "Knowledge base" },
  { key: "knowledgeConnectors", label: "Live sources" },
  { key: "liveInsightCardsEnabled", label: "Live insight cards" },
  { key: "guardsPrompt", label: "Guards" },
  { key: "guardrailRedirectStyle", label: "Guard redirect style" },
  { key: "guardrailRedirectText", label: "Guard fixed line" },
  { key: "guardrailRedirectGuidance", label: "Guard adaptive guidance" },
  { key: "semanticPrompt", label: "EOT classifier" },
  { key: "briefComplete", label: "Brief complete" },
  { key: "incompleteTails", label: "Incomplete tails" },
  { key: "semanticNudgeText", label: "Semantic nudge" },
  { key: "hangupPrompt", label: "Hangup intent" },
  { key: "affirmPrompt", label: "Hangup affirm" },
  { key: "hangupConfirmText", label: "Confirm line" },
  { key: "hangupGoodbyeText", label: "Goodbye line" },
  { key: "affectPrompt", label: "Affect" },
  { key: "videoPerceptionFocus", label: "Video focus" },
  { key: "videoPerceptionRules", label: "Video rules" },
  { key: "videoPerceptionChips", label: "Video chips" },
  { key: "sessionMaxMinutes", label: "Session max (min)" },
  { key: "sessionWarnMinutes", label: "Warn before end (min)" },
  { key: "idleSeconds", label: "Idle seconds" },
  { key: "idleMaxNudges", label: "Idle max nudges" },
  { key: "idlePrompt", label: "Idle nudge" },
  { key: "idleGoodbyeText", label: "Idle goodbye" },
  { key: "voiceId", label: "Voice ID" },
  { key: "voiceName", label: "Voice name" },
  { key: "ttsSpeed", label: "TTS speed" },
  { key: "ttsVolume", label: "TTS volume" },
  { key: "ttsEmotion", label: "TTS emotion" },
  { key: "videoAvatarEnabled", label: "Video avatar enabled" },
  { key: "liveAvatarSandbox", label: "LiveAvatar sandbox" },
  { key: "liveAvatarId", label: "LiveAvatar ID" },
  { key: "liveAvatarStatus", label: "LiveAvatar status" },
];

type CatalogRow = {
  agentId: string;
  name: string;
  role: string;
  desc: string;
  color: string;
  version: number;
  published: boolean;
  archived: boolean;
  sessions30d: number;
  completed30d: number;
  successPct: number | null;
  sessionMaxMinutes: number;
  publishedAt?: string | null;
  avatarUrl?: string | null;
  avatarKey?: string | null;
  voiceId?: string | null;
  voiceName?: string | null;
  ttsSpeed?: number | null;
  ttsVolume?: number | null;
  ttsEmotion?: string | null;
  videoAvatarEnabled?: boolean;
  liveAvatarSandbox?: boolean;
  liveAvatarId?: string | null;
  liveAvatarStatus?: string | null;
  guardrailEnabled?: boolean;
  guardrailRedirectStyle?: "fixed" | "adaptive";
  idleEnabled?: boolean;
  idleSeconds?: number;
  agentMode?: AgentMode;
  directVoice?: string | null;
  elevenLabsVoiceId?: string | null;
};

function agentEditSnapshot(agent: Agent): string {
  const {
    updatedAt: _u,
    createdAt: _c,
    publishedAt: _p,
    ...rest
  } = agent;
  return JSON.stringify(rest);
}

function agentIdFor(name: string) {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return cleaned.toLowerCase() || `agent-${Date.now()}`;
}

function uniqueAgentId(name: string, existingIds: Set<string>): string {
  const base = agentIdFor(name);
  const taken = (id: string) => existingIds.has(id.toLowerCase());
  if (!taken(base)) return base;
  let n = 2;
  while (taken(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

function agentInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0]![0] || ""}${parts[1]![0] || ""}`.toUpperCase();
  return (name.trim().slice(0, 2) || "?").toUpperCase();
}

function agentCapabilitySummary(a: {
  guardrailEnabled?: boolean;
  idleEnabled?: boolean;
  idleSeconds?: number;
  sessionMaxMinutes?: number;
  voiceName?: string | null;
  voiceId?: string | null;
  role?: string;
}): string {
  const bits = [
    a.role?.trim() || "Custom",
    a.guardrailEnabled ? "Guards on" : "Guards off",
    a.idleEnabled !== false ? `Idle ${a.idleSeconds ?? 8}s` : "Idle off",
    `${clampMinutes(a.sessionMaxMinutes, 15)} min`,
    a.voiceName || (a.voiceId ? "Custom voice" : "Workspace voice"),
  ];
  return bits.join(" · ");
}

function buildCreatePayload(
  source: Agent | null,
  agentId: string,
  name: string,
  role: string,
  blankMode: AgentMode = DEFAULT_AGENT_MODE,
): Agent {
  if (!source) {
    return withDefaults({
      agentId,
      name,
      role: role.trim() || "Custom",
      agentMode: blankMode,
      published: false,
      version: 0,
      publishedAt: null,
      archived: false,
      archivedAt: null,
    });
  }
  const {
    agentId: _aid,
    name: _name,
    published: _pub,
    version: _ver,
    publishedAt: _pa,
    archived: _arch,
    archivedAt: _aa,
    createdAt: _ca,
    updatedAt: _ua,
    avatarUrl: _url,
    ...rest
  } = source;
  return withDefaults({
    ...rest,
    agentId,
    name,
    role: role.trim() || String(source.role || "").trim() || "Custom",
    published: false,
    version: 0,
    publishedAt: null,
    archived: false,
    archivedAt: null,
    avatarKey: source.avatarKey || null,
  });
}

/** Encode upload as landscape JPEG for 16:9 cover + avatar (no aspect validation). */
async function fileToCoverJpeg(file: File): Promise<string> {
  const bmp = await createImageBitmap(file);
  const maxW = 1600;
  const maxH = 900;
  let w = bmp.width;
  let h = bmp.height;
  const scale = Math.min(1, maxW / w, maxH / h);
  w = Math.max(1, Math.round(w * scale));
  h = Math.max(1, Math.round(h * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bmp.close();
    throw new Error("Could not read image");
  }
  ctx.drawImage(bmp, 0, 0, w, h);
  bmp.close();
  const encoded = canvas.toDataURL("image/jpeg", 0.88).split(",")[1];
  if (!encoded) throw new Error("Could not encode image");
  return encoded;
}

function withDefaults(partial: Partial<Agent> & { agentId: string; name: string }): Agent {
  const persona = promptsForAgent(partial.agentId, partial.name);
  const metaRole = PERSONA_META[String(partial.agentId || "")]?.role || "";
  const mode = normalizeAgentMode(partial.agentMode);
  return {
    ...persona,
    ...partial,
    agentId: partial.agentId,
    name: partial.name,
    role: (partial.role || "").trim() || metaRole,
    agentMode: mode,
    directModel: (partial.directModel || "").trim() || DIRECT_MODEL_DEFAULT,
    directVoice: (partial.directVoice || "").trim() || DIRECT_VOICE_DEFAULT,
    elevenLabsVoiceId: partial.elevenLabsVoiceId || null,
    liveAvatarVoiceId: partial.liveAvatarVoiceId || null,
    liveAvatarContextId: partial.liveAvatarContextId || null,
    liveAvatarPrompt: partial.liveAvatarPrompt || "",
    liveAvatarLinks: Array.isArray(partial.liveAvatarLinks) ? partial.liveAvatarLinks : [],
    liveInsightCardsEnabled: partial.liveInsightCardsEnabled === true,
    published: partial.published ?? false,
    sessionMaxMinutes: clampMinutes(partial.sessionMaxMinutes, 15),
    sessionWarnMinutes: clampWarnMinutes(
      partial.sessionWarnMinutes,
      clampMinutes(partial.sessionMaxMinutes, 15),
      1,
    ),
    idleEnabled: partial.idleEnabled ?? true,
    idleSeconds: clampIdleSeconds(partial.idleSeconds, 8),
    idleMaxNudges: clampIdleMaxNudges(partial.idleMaxNudges, 1),
    semanticNudgeText: (partial.semanticNudgeText || persona.semanticNudgeText || "Yes?").trim(),
    idlePrompt: (partial.idlePrompt || persona.idlePrompt || "Are you still here?").trim(),
    idleGoodbyeText: (
      partial.idleGoodbyeText ||
      persona.idleGoodbyeText ||
      "Okay, I think you've stepped away. Goodbye!"
    ).trim(),
    guardrailEnabled:
      mode === "direct"
        ? Boolean((partial.guardsPrompt || "").trim())
        : partial.guardrailEnabled === true,
    guardrailRedirectStyle:
      mode === "direct"
        ? "adaptive"
        : (partial.guardrailRedirectStyle || persona.guardrailRedirectStyle || "fixed") === "adaptive"
          ? "adaptive"
          : "fixed",
    guardrailRedirectText: (
      partial.guardrailRedirectText
      || persona.guardrailRedirectText
      || "I can't help with that. What else can I help you with?"
    ).trim(),
    guardrailRedirectGuidance: (
      partial.guardrailRedirectGuidance
      || persona.guardrailRedirectGuidance
      || "Refuse briefly and warmly. Do not lecture. Offer to help with something else in one short spoken sentence."
    ).trim(),
    version: partial.version ?? 0,
  };
}

function clampMinutes(v: unknown, fallback = 15): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(3, Math.min(30, Math.round(n)));
}

function clampWarnMinutes(v: unknown, sessionMax: number, fallback = 1): number {
  const maxLead = Math.max(1, clampMinutes(sessionMax, 15) - 1);
  const n = Number(v);
  const base = Number.isFinite(n) ? Math.round(n) : fallback;
  return Math.max(1, Math.min(maxLead, base));
}

function clampIdleSeconds(v: unknown, fallback = 8): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(3, Math.min(60, Math.round(n)));
}

function clampIdleMaxNudges(v: unknown, fallback = 1): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(1, Math.min(3, Math.round(n)));
}

function fillPromptDefaults(existing: Agent | undefined, agentId: string, name: string): Agent {
  const e = existing || ({} as Partial<Agent>);
  const persona = promptsForAgent(agentId, name);
  const sharedMedicalGreeting = DEFAULT_AGENT_PROMPTS.greetingText;
  const sharedMedicalSystem = DEFAULT_AGENT_PROMPTS.systemPrompt;
  const pick = (key: keyof typeof persona, stored?: string | null): string => {
    const v = (stored || "").trim();
    const fallback = String(persona[key] ?? "");
    if (!v) return fallback;
    if (
      (key === "greetingText" || key === "systemPrompt" || key === "guardsPrompt" || key === "knowledgeBase" || key === "hangupGoodbyeText") &&
      (v === sharedMedicalGreeting || v === sharedMedicalSystem || v === DEFAULT_AGENT_PROMPTS.guardsPrompt)
    ) {
      return fallback;
    }
    return v;
  };
  return withDefaults({
    agentId,
    name,
    ...e,
    greetingText: pick("greetingText", e.greetingText),
    systemPrompt: pick("systemPrompt", e.systemPrompt),
    knowledgeBase: pick("knowledgeBase", e.knowledgeBase),
    knowledgeConnectors: Array.isArray(e.knowledgeConnectors) ? e.knowledgeConnectors : [],
    liveInsightCardsEnabled: e.liveInsightCardsEnabled === true,
    guardsPrompt: pick("guardsPrompt", e.guardsPrompt),
    semanticPrompt: e.semanticPrompt || persona.semanticPrompt,
    briefComplete: e.briefComplete || persona.briefComplete,
    incompleteTails: e.incompleteTails || persona.incompleteTails,
    hangupPrompt: e.hangupPrompt || persona.hangupPrompt,
    affirmPrompt: e.affirmPrompt || persona.affirmPrompt,
    affectPrompt: e.affectPrompt || persona.affectPrompt,
    videoPerceptionFocus: e.videoPerceptionFocus || persona.videoPerceptionFocus,
    videoPerceptionRules: e.videoPerceptionRules || persona.videoPerceptionRules,
    videoPerceptionChips: e.videoPerceptionChips || persona.videoPerceptionChips,
    hangupConfirmText: e.hangupConfirmText || persona.hangupConfirmText,
    hangupGoodbyeText: pick("hangupGoodbyeText", e.hangupGoodbyeText),
    semanticNudgeText: e.semanticNudgeText || persona.semanticNudgeText,
    idlePrompt: e.idlePrompt || persona.idlePrompt,
    idleGoodbyeText: e.idleGoodbyeText || persona.idleGoodbyeText,
    guardrailEnabled:
      normalizeAgentMode(e.agentMode) === "direct"
        ? Boolean((e.guardsPrompt || "").trim())
        : e.guardrailEnabled === true,
    guardrailRedirectStyle:
      normalizeAgentMode(e.agentMode) === "direct"
        ? "adaptive"
        : (e.guardrailRedirectStyle || persona.guardrailRedirectStyle || "fixed") === "adaptive"
          ? "adaptive"
          : "fixed",
    guardrailRedirectText:
      e.guardrailRedirectText || persona.guardrailRedirectText,
    guardrailRedirectGuidance:
      e.guardrailRedirectGuidance || persona.guardrailRedirectGuidance,
    voiceId: e.voiceId || null,
    voiceName: e.voiceName || null,
    ttsSpeed: clampTtsSpeed(e.ttsSpeed, 1),
    ttsVolume: clampTtsVolume(e.ttsVolume, 1),
    ttsEmotion: e.ttsEmotion || null,
    videoAvatarEnabled: e.videoAvatarEnabled === true,
    liveAvatarSandbox: e.liveAvatarSandbox === true,
    liveAvatarId: e.liveAvatarId || null,
    liveAvatarStatus: e.liveAvatarStatus || "none",
    liveAvatarError: e.liveAvatarError || null,
    avatarUrl: resolveAgentAvatarUrl(e),
    avatarKey: e.avatarKey || null,
    sessionMaxMinutes: clampMinutes(e.sessionMaxMinutes, 15),
    sessionWarnMinutes: clampWarnMinutes(e.sessionWarnMinutes, clampMinutes(e.sessionMaxMinutes, 15), 1),
    idleEnabled: e.idleEnabled ?? true,
    idleSeconds: clampIdleSeconds(e.idleSeconds, 8),
    idleMaxNudges: clampIdleMaxNudges(e.idleMaxNudges, 1),
    agentMode: normalizeAgentMode(e.agentMode),
    directModel: e.directModel || DIRECT_MODEL_DEFAULT,
    directVoice: e.directVoice || DIRECT_VOICE_DEFAULT,
    elevenLabsVoiceId: e.elevenLabsVoiceId || null,
    liveAvatarVoiceId: e.liveAvatarVoiceId || null,
    liveAvatarContextId: e.liveAvatarContextId || null,
    liveAvatarPrompt: e.liveAvatarPrompt || "",
    liveAvatarLinks: Array.isArray(e.liveAvatarLinks) ? e.liveAvatarLinks : [],
    version: e.version ?? 0,
    publishedAt: e.publishedAt,
  });
}

function buildCatalog(
  agents: Agent[],
  sessionCounts: Record<string, { total: number; completed: number }>,
): CatalogRow[] {
  const byId = new Map(agents.map((a) => [String(a.agentId || ""), a]));
  const byName = new Map(agents.map((a) => [String(a.name || "").toLowerCase(), a]));
  const seenIds = new Set<string>();
  const rows: CatalogRow[] = [];

  const pushRow = (agentId: string, fallbackName: string) => {
    const live = byId.get(String(agentId || "").trim())
      || byName.get(fallbackName.toLowerCase());
    const resolvedId = String(live?.agentId || agentId || "").trim();
    if (!resolvedId || seenIds.has(resolvedId)) return;
    seenIds.add(resolvedId);
    const meta = PERSONA_META[resolvedId] || {
      role: "Custom",
      desc: live?.systemPrompt?.slice(0, 80) || "Custom agent.",
      color: "#607388",
    };
    const counts = sessionCounts[resolvedId] || { total: 0, completed: 0 };
    const successPct = counts.total > 0 ? Math.round((counts.completed / counts.total) * 1000) / 10 : null;
    rows.push({
      agentId: resolvedId,
      name: live?.name || fallbackName,
      role: (live?.role || "").trim() || meta.role,
      desc: meta.desc,
      color: meta.color,
      version: live?.version ?? 0,
      published: Boolean(live?.published),
      archived: live?.archived === true,
      sessions30d: counts.total,
      completed30d: counts.completed,
      successPct,
      sessionMaxMinutes: clampMinutes(live?.sessionMaxMinutes, 15),
      publishedAt: live?.publishedAt,
      avatarUrl: resolveAgentAvatarUrl(live || {}),
      avatarKey: live?.avatarKey || null,
      voiceId: live?.voiceId || null,
      voiceName: live?.voiceName || null,
      ttsSpeed: live?.ttsSpeed ?? null,
      ttsVolume: live?.ttsVolume ?? null,
      ttsEmotion: live?.ttsEmotion || null,
      videoAvatarEnabled: live?.videoAvatarEnabled === true,
      liveAvatarSandbox: live?.liveAvatarSandbox === true,
      liveAvatarId: live?.liveAvatarId || null,
      liveAvatarStatus: live?.liveAvatarStatus || "none",
      guardrailEnabled: live?.guardrailEnabled === true,
      guardrailRedirectStyle:
        live?.guardrailRedirectStyle === "adaptive" ? "adaptive" : "fixed",
      idleEnabled: live?.idleEnabled !== false,
      idleSeconds: clampIdleSeconds(live?.idleSeconds, 8),
      agentMode: normalizeAgentMode(live?.agentMode),
      directVoice: live?.directVoice || null,
      elevenLabsVoiceId: live?.elevenLabsVoiceId || null,
    });
  };

  for (const seed of PERSONA_SEED) {
    pushRow(seed.agentId, seed.fallbackName);
  }
  for (const a of agents) {
    if (a.agentId) pushRow(a.agentId, a.name || a.agentId);
  }
  return rows;
}

/** Keep a just-uploaded cover if a refetch briefly omits avatarUrl (Scan lag). */
function mergeCatalogAvatars(prev: CatalogRow[], next: CatalogRow[]): CatalogRow[] {
  if (!prev.length) return next;
  const prevById = new Map(prev.map((r) => [String(r.agentId || "").toLowerCase(), r]));
  return next.map((row) => {
    if (row.avatarUrl || row.avatarKey) return row;
    const old = prevById.get(String(row.agentId || "").toLowerCase());
    if (!old?.avatarUrl && !old?.avatarKey) return row;
    return {
      ...row,
      avatarUrl: old.avatarUrl || resolveAgentAvatarUrl(old),
      avatarKey: old.avatarKey || null,
    };
  });
}

export function AgentsPage() {
  const profile = useAuthProfile();
  const { impersonation } = useRoleImpersonation();
  const canTestCall = hasPermission(profile?.permissions, "agents.test_call");
  const [selected, setSelected] = useState("Nova");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"grid" | "list">("grid");
  const [open, setOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createRole, setCreateRole] = useState("");
  const [createFrom, setCreateFrom] = useState("blank");
  const [createMode, setCreateMode] = useState<AgentMode>(DEFAULT_AGENT_MODE);
  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneName, setCloneName] = useState("");
  const [cloneRole, setCloneRole] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [releaseMenuOpen, setReleaseMenuOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const releaseMenuRef = useRef<HTMLDivElement | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [settings, setSettings] = useState<Settings>({});
  const [draft, setDraft] = useState<Agent | null>(null);
  const [editSection, setEditSection] = useState<AgentEditSectionId>("conversation");
  const [editBaseline, setEditBaseline] = useState<string | null>(null);
  /** Editor target: working meta, or a published snapshot number. */
  const [viewingVersion, setViewingVersion] = useState<"working" | number>("working");
  const [editVersionOptions, setEditVersionOptions] = useState<
    Array<{ version: number; publishedAt?: string | null }>
  >([]);
  const [overwriteOpen, setOverwriteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const deepLinkHandled = useRef<string | null>(null);
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [evalPassRate, setEvalPassRate] = useState<number | null>(null);
  const [evalTotal, setEvalTotal] = useState(0);
  const [scoreOpen, setScoreOpen] = useState(false);
  const scoreRef = useRef<HTMLDivElement | null>(null);
  const [versions, setVersions] = useState<AgentVersion[]>([]);
  const [leftVer, setLeftVer] = useState<number | null>(null);
  const [rightVer, setRightVer] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const current = catalog.find((a) => a.name === selected) || catalog[0];
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return catalog.filter((a) => {
      if (!showArchived && a.archived) return false;
      return `${a.name} ${a.role}`.toLowerCase().includes(q);
    });
  }, [catalog, query, showArchived]);
  const archivedCount = useMemo(() => catalog.filter((a) => a.archived).length, [catalog]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2800);
  };

  const uploadAvatar = async (agentId: string, file: File) => {
    setSaving(true);
    try {
      const dataBase64 = await fileToCoverJpeg(file);
      const saved = await api.uploadAgentAvatar(agentId, dataBase64);
      const nextUrl = resolveAgentAvatarUrl(saved);
      const nextKey = saved.avatarKey || null;
      const idKey = String(agentId || "").toLowerCase();
      setCatalog((rows) => rows.map((r) => (
        String(r.agentId || "").toLowerCase() === idKey
          ? { ...r, avatarUrl: nextUrl, avatarKey: nextKey }
          : r
      )));
      setDraft((d) => (d && String(d.agentId || "").toLowerCase() === idKey
        ? { ...d, avatarUrl: nextUrl, avatarKey: nextKey }
        : d));
      showToast("Cover / avatar updated");
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Cover upload failed");
    } finally {
      setSaving(false);
    }
  };

  // Do NOT depend on `selected` — switching agents must not refetch (that wiped covers
  // when Scan lagged behind a just-written avatarKey).
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsLive, agentsLive] = await Promise.all([
        api.getSettings().catch(() => ({} as Settings)),
        api.listAgents(),
      ]);
      setSettings(settingsLive);
      let agentList = agentsLive.agents || [];
      if (impersonation) {
        agentList = filterAgentsByAllowlist(agentList, profile?.allowedAgentIds);
      }
      const rows = buildCatalog(agentList, {});
      setCatalog((prev) => mergeCatalogAvatars(prev, rows));
      const sel = selectedRef.current;
      if (!rows.find((r) => r.name === sel) && rows[0]) setSelected(rows[0].name);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to load agents");
    } finally {
      setLoading(false);
    }

    // Session/30d counts + eval stats must not block the library paint.
    void (async () => {
      try {
        const [countsLive, checkupsLive] = await Promise.all([
          api.sessionAgentCounts(30).catch(() => ({ counts: {} as Record<string, { total: number; completed: number }> })),
          api.listCheckups(CHECKUP_PROTOCOLS.length).catch(() => null),
        ]);
        const counts = countsLive.counts || {};
        setCatalog((prev) => {
          if (!prev.length) return prev;
          return prev.map((row) => {
            const c = counts[String(row.agentId || "").toLowerCase()] || { total: 0, completed: 0 };
            const successPct = c.total > 0 ? Math.round((c.completed / c.total) * 1000) / 10 : null;
            return {
              ...row,
              sessions30d: c.total,
              completed30d: c.completed,
              successPct,
            };
          });
        });
        if (checkupsLive?.stats) {
          setEvalPassRate(checkupsLive.stats.passRate);
          setEvalTotal(checkupsLive.stats.totalResults || 0);
        } else {
          setEvalPassRate(null);
          setEvalTotal(0);
        }
      } catch {
        /* secondary metrics are best-effort */
      }
    })();
  }, [impersonation, profile?.allowedAgentIds]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  useEffect(() => {
    const mode = normalizeAgentMode(draft?.agentMode);
    if (mode === "direct" && editSection === "endpointing") {
      setEditSection("conversation");
    }
    if (
      mode === "liveavatar" &&
      (editSection === "endpointing" || editSection === "callControl" || editSection === "guards")
    ) {
      setEditSection("conversation");
    }
  }, [draft?.agentMode, editSection]);

  useEffect(() => {
    setScoreOpen(false);
    setReleaseMenuOpen(false);
  }, [selected]);

  useEffect(() => {
    if (!releaseMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (releaseMenuRef.current?.contains(t)) return;
      setReleaseMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setReleaseMenuOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [releaseMenuOpen]);

  useEffect(() => {
    if (!scoreOpen) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (scoreRef.current?.contains(t)) return;
      setScoreOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setScoreOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [scoreOpen]);

  const productionCount = catalog.filter((a) => a.published && !a.archived).length;
  const draftCount = catalog.filter((a) => !a.archived && (!a.published || a.version === 0)).length;
  const versionSum = catalog.reduce((n, a) => n + (a.version || 0), 0);

  async function openEditor(opts?: {
    agentId?: string;
    version?: number | "working";
    section?: AgentEditSectionId;
  }) {
    const agentId = opts?.agentId || current?.agentId;
    if (!agentId) return;
    const catalogRow = catalog.find((a) => a.agentId === agentId) || current;
    if (catalogRow && catalogRow.name !== selected) setSelected(catalogRow.name);
    setEditSection(opts?.section || "conversation");
    setSaving(true);
    try {
      const [existing, verRes] = await Promise.all([
        api.getAgent(agentId),
        api.listAgentVersions(agentId).catch(() => ({ versions: [] as AgentVersion[] })),
      ]);
      const name = existing?.name || catalogRow?.name || agentId;
      const working = fillPromptDefaults(existing, agentId, name);
      const publishedVers = (verRes.versions || [])
        .slice()
        .sort((a, b) => Number(b.version) - Number(a.version));
      setEditVersionOptions(
        publishedVers.map((v) => ({ version: Number(v.version) || 0, publishedAt: v.publishedAt })),
      );

      const want = opts?.version;
      if (typeof want === "number" && want > 0) {
        const snap = await api.getAgentVersion(agentId, want);
        const next = fillPromptDefaults(snap, agentId, name);
        setDraft(next);
        setEditBaseline(agentEditSnapshot(next));
        setViewingVersion(want);
      } else {
        setDraft(working);
        setEditBaseline(agentEditSnapshot(working));
        setViewingVersion("working");
      }
      setEditOpen(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to load agent");
    } finally {
      setSaving(false);
    }
  }

  async function switchViewingVersion(next: "working" | number) {
    if (!draft || next === viewingVersion) return;
    if (editDirty) {
      const ok = window.confirm("Discard unsaved changes and switch version?");
      if (!ok) return;
    }
    setSaving(true);
    try {
      if (next === "working") {
        const existing = await api.getAgent(draft.agentId);
        const loaded = fillPromptDefaults(existing, draft.agentId, draft.name);
        setDraft(loaded);
        setEditBaseline(agentEditSnapshot(loaded));
        setViewingVersion("working");
      } else {
        const snap = await api.getAgentVersion(draft.agentId, next);
        const loaded = fillPromptDefaults(snap, draft.agentId, draft.name);
        setDraft(loaded);
        setEditBaseline(agentEditSnapshot(loaded));
        setViewingVersion(next);
      }
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Failed to load version");
    } finally {
      setSaving(false);
    }
  }

  function openAdminTestCall(agentId: string, version: "working" | number) {
    if (!canTestCall) {
      showToast("Your role cannot start Test call");
      return;
    }
    const av = version === "working" ? "working" : String(version);
    window.open(
      `${PULSE_ORIGIN}/?agent=${encodeURIComponent(agentId)}&av=${encodeURIComponent(av)}`,
      "_blank",
      "noopener,noreferrer",
    );
  }

  async function openCompare() {
    if (!current) return;
    setSaving(true);
    try {
      const [res, live] = await Promise.all([
        api.listAgentVersions(current.agentId, { full: true }),
        api.getAgent(current.agentId),
      ]);
      const published = (res.versions || []).slice().sort((a, b) => Number(b.version) - Number(a.version));
      const working: AgentVersion | null = live
        ? {
            ...live,
            version: 0,
            publishedAt: live.updatedAt || null,
            publishedBy: "working copy",
            sk: "meta",
          }
        : null;
      // Version 0 = current working meta (not yet a new publish)
      const list = working ? [working, ...published] : published;
      setVersions(list);
      if (published.length >= 1 && working) {
        setLeftVer(published[0]?.version ?? null);
        setRightVer(0);
      } else if (published.length >= 2) {
        setLeftVer(published[1]?.version ?? null);
        setRightVer(published[0]?.version ?? null);
      } else if (published.length === 1) {
        setLeftVer(published[0]?.version ?? null);
        setRightVer(published[0]?.version ?? null);
      } else {
        setLeftVer(working ? 0 : null);
        setRightVer(working ? 0 : null);
      }
      setCompareOpen(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "No versions yet — publish a release first");
    } finally {
      setSaving(false);
    }
  }

  const editDirty = Boolean(
    draft && editBaseline && agentEditSnapshot(draft) !== editBaseline,
  );

  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || loading || !catalog.length) return;
    const verRaw = searchParams.get("version");
    const ver = verRaw && /^\d+$/.test(verRaw) ? Number(verRaw) : "working";
    const key = `${editId}:${ver}`;
    if (deepLinkHandled.current === key) return;
    deepLinkHandled.current = key;
    const row = catalog.find((a) => a.agentId === editId);
    if (row) setSelected(row.name);
    void openEditor({
      agentId: editId,
      version: ver === "working" ? "working" : ver,
    }).then(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("edit");
      next.delete("version");
      setSearchParams(next, { replace: true });
    });
  }, [searchParams, loading, catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  async function persistWorkingDraft(asDraft: boolean) {
    if (!draft) return;
    const mode = normalizeAgentMode(draft.agentMode);
    if (mode === "liveavatar") {
      const avatarReady =
        draft.videoAvatarEnabled === true &&
        String(draft.liveAvatarStatus || "") === "ready" &&
        Boolean(String(draft.liveAvatarId || "").trim());
      if (!avatarReady) {
        showToast("LiveAvatar agents require a ready Video avatar bind before Save.");
        return;
      }
      if (!String(draft.elevenLabsVoiceId || "").trim()) {
        showToast("LiveAvatar agents require an ElevenLabs voice before Save.");
        return;
      }
    }
    const payload = {
      ...draft,
      agentMode: mode,
      videoAvatarEnabled: mode === "liveavatar" ? true : draft.videoAvatarEnabled === true,
      elevenLabsVoiceId: draft.elevenLabsVoiceId || null,
      liveAvatarVoiceId: draft.liveAvatarVoiceId || null,
      liveAvatarContextId: draft.liveAvatarContextId || null,
      liveAvatarPrompt: draft.liveAvatarPrompt || "",
      liveAvatarLinks: Array.isArray(draft.liveAvatarLinks) ? draft.liveAvatarLinks : [],
      sessionMaxMinutes: clampMinutes(draft.sessionMaxMinutes, 15),
      sessionWarnMinutes: clampWarnMinutes(
        draft.sessionWarnMinutes,
        clampMinutes(draft.sessionMaxMinutes, 15),
        1,
      ),
      idleSeconds: clampIdleSeconds(draft.idleSeconds, 8),
      idleMaxNudges: clampIdleMaxNudges(draft.idleMaxNudges, 1),
      ttsSpeed: clampTtsSpeed(draft.ttsSpeed, 1),
      ttsVolume: clampTtsVolume(draft.ttsVolume, 1),
      ttsEmotion: draft.ttsEmotion || null,
      voiceId: draft.voiceId || null,
      voiceName: draft.voiceName || null,
      semanticNudgeText: (draft.semanticNudgeText || "Yes?").trim(),
      idlePrompt: (draft.idlePrompt || "Are you still here?").trim(),
      idleGoodbyeText: (
        draft.idleGoodbyeText ||
        "Okay, I think you've stepped away. Goodbye!"
      ).trim(),
      guardrailRedirectText: (
        draft.guardrailRedirectText
        || "I can't help with that. What else can I help you with?"
      ).trim(),
      guardrailRedirectGuidance: (
        draft.guardrailRedirectGuidance
        || "Refuse briefly and warmly. Do not lecture. Offer to help with something else in one short spoken sentence."
      ).trim(),
      guardrailRedirectStyle: (
        normalizeAgentMode(draft.agentMode) === "direct"
        || draft.guardrailRedirectStyle === "adaptive"
          ? "adaptive"
          : "fixed"
      ) as "fixed" | "adaptive",
      guardrailEnabled:
        normalizeAgentMode(draft.agentMode) === "direct"
          ? Boolean((draft.guardsPrompt || "").trim())
          : draft.guardrailEnabled === true,
      // Editing published working copy (or restoring a snapshot) creates/keeps a draft until Publish.
      ...(asDraft ? { published: false } : {}),
    };
    const saved = await api.updateAgent(draft.agentId, payload).catch(() => api.upsertAgent(payload));
    const next = withDefaults({ ...saved, name: draft.name });
    setDraft(next);
    setEditBaseline(agentEditSnapshot(next));
    setViewingVersion("working");
    showToast(asDraft ? `Saved draft of ${draft.name}` : `Saved ${draft.name}`);
    setEditOpen(false);
    setOverwriteOpen(false);
    await loadAll();
  }

  async function saveDraft() {
    if (!draft) return;
    if (viewingVersion !== "working") {
      setOverwriteOpen(true);
      return;
    }
    setSaving(true);
    try {
      // Auto-draft: saving changes on a previously published working copy unpublishes until Publish.
      const asDraft = Boolean(current?.published) || Boolean(draft.published);
      await persistWorkingDraft(asDraft);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function confirmOverwriteDraft() {
    setSaving(true);
    try {
      await persistWorkingDraft(true);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function publish() {
    if (!current) return;
    if (current.archived) {
      showToast("Unarchive this agent before publishing");
      return;
    }
    // Publishing while viewing a historical snapshot would snapshot the wrong config.
    if (draft && draft.agentId === current.agentId && viewingVersion !== "working") {
      showToast("Switch to Working copy before publishing");
      return;
    }
    setPublishConfirmOpen(false);
    setSaving(true);
    try {
      // Only persist when the editor has the working copy open. Never synthesize a
      // sparse/default payload here — that wiped voice, prompts, and other fields.
      if (draft && draft.agentId === current.agentId) {
        const payload = {
          ...draft,
          sessionMaxMinutes: clampMinutes(draft.sessionMaxMinutes, 15),
          sessionWarnMinutes: clampWarnMinutes(
            draft.sessionWarnMinutes,
            clampMinutes(draft.sessionMaxMinutes, 15),
            1,
          ),
          idleSeconds: clampIdleSeconds(draft.idleSeconds, 8),
          idleMaxNudges: clampIdleMaxNudges(draft.idleMaxNudges, 1),
          ttsSpeed: clampTtsSpeed(draft.ttsSpeed, 1),
          ttsVolume: clampTtsVolume(draft.ttsVolume, 1),
          ttsEmotion: draft.ttsEmotion || null,
          voiceId: draft.voiceId || null,
          voiceName: draft.voiceName || null,
          semanticNudgeText: (draft.semanticNudgeText || "Yes?").trim(),
          idlePrompt: (draft.idlePrompt || "Are you still here?").trim(),
          idleGoodbyeText: (
            draft.idleGoodbyeText ||
            "Okay, I think you've stepped away. Goodbye!"
          ).trim(),
          guardrailRedirectText: (
            draft.guardrailRedirectText
            || "I can't help with that. What else can I help you with?"
          ).trim(),
          guardrailRedirectGuidance: (
            draft.guardrailRedirectGuidance
            || "Refuse briefly and warmly. Do not lecture. Offer to help with something else in one short spoken sentence."
          ).trim(),
          guardrailRedirectStyle: (
            normalizeAgentMode(draft.agentMode) === "direct"
            || draft.guardrailRedirectStyle === "adaptive"
              ? "adaptive"
              : "fixed"
          ) as "fixed" | "adaptive",
          guardrailEnabled:
            normalizeAgentMode(draft.agentMode) === "direct"
              ? Boolean((draft.guardsPrompt || "").trim())
              : draft.guardrailEnabled === true,
        };
        await api.updateAgent(current.agentId, payload);
      }
      const published = await api.publishAgent(current.agentId);
      const nextVer = published.version || (current.version || 0) + 1;
      showToast(`${current.name} v${nextVer} published — open Compare to diff`);
      if (draft && draft.agentId === current.agentId) {
        const next = withDefaults({ ...published, name: draft.name });
        setDraft(next);
        setEditBaseline(agentEditSnapshot(next));
      }
      await loadAll();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setSaving(false);
    }
  }

  const isDefault = current ? settings.defaultAgentId === current.agentId : false;

  async function setDefaultAgent() {
    if (!current) return;
    if (current.archived) {
      showToast("Archived agents cannot be the workspace default");
      return;
    }
    setSaving(true);
    setReleaseMenuOpen(false);
    try {
      const next = await api.putSettings({
        ...settings,
        defaultAgentId: current.agentId,
      });
      setSettings(next);
      showToast(`${current.name} is the default for new sessions`);
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not update default agent");
    } finally {
      setSaving(false);
    }
  }

  async function setAgentArchived(nextArchived: boolean) {
    if (!current) return;
    setSaving(true);
    setReleaseMenuOpen(false);
    try {
      await api.updateAgent(current.agentId, { archived: nextArchived });
      if (nextArchived && settings.defaultAgentId === current.agentId) {
        const next = await api.putSettings({ ...settings, defaultAgentId: null });
        setSettings(next);
      }
      showToast(nextArchived ? `${current.name} archived` : `${current.name} restored`);
      await loadAll();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not update archive state");
    } finally {
      setSaving(false);
    }
  }

  function openCreateDialog() {
    setCreateName("");
    setCreateRole("");
    setCreateFrom("blank");
    setCreateMode(DEFAULT_AGENT_MODE);
    setOpen(true);
  }

  async function persistNewAgent(source: Agent | null, name: string, role: string) {
    const existingIds = new Set(
      catalog.map((a) => String(a.agentId || "").toLowerCase()).filter(Boolean),
    );
    const agentId = uniqueAgentId(name, existingIds);
    const payload = buildCreatePayload(source, agentId, name, role, createMode);
    const {
      avatarUrl: _ignoreUrl,
      createdAt: _c,
      updatedAt: _u,
      publishedAt: _p,
      ...body
    } = payload;
    return api.upsertAgent({
      ...body,
      agentId,
      name,
      role: payload.role,
      published: false,
      version: 0,
      avatarKey: source?.avatarKey || null,
      ...(source?.agentId ? { clonedFrom: source.agentId } : {}),
    } as Parameters<typeof api.upsertAgent>[0] & { clonedFrom?: string });
  }

  async function createDraft() {
    const name = createName.trim();
    if (!name) {
      showToast("Enter an agent name");
      return;
    }
    setSaving(true);
    try {
      let source: Agent | null = null;
      if (createFrom && createFrom !== "blank") {
        try {
          source = await api.getAgent(createFrom);
        } catch {
          source = fillPromptDefaults(undefined, createFrom, createFrom);
        }
      }
      const role =
        createRole.trim()
        || (source?.role || "").trim()
        || "";
      const saved = await persistNewAgent(source, name, role);
      setOpen(false);
      showToast(`${saved.name || name} draft created`);
      await loadAll();
      setSelected(saved.name || name);
      await openEditor({ agentId: saved.agentId });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not create agent");
    } finally {
      setSaving(false);
    }
  }

  function openCloneFromEditor() {
    if (!draft) return;
    const base = (draft.name || "Agent").trim() || "Agent";
    setCloneName(`${base} copy`);
    setCloneRole((draft.role || "").trim() || (current?.role || "").trim());
    setCloneOpen(true);
  }

  async function confirmCloneFromEditor() {
    if (!draft) return;
    const name = cloneName.trim();
    if (!name) {
      showToast("Enter an agent name");
      return;
    }
    const sourceLabel =
      viewingVersion === "working"
        ? "working copy"
        : `v${viewingVersion}`;
    setSaving(true);
    try {
      const saved = await persistNewAgent(draft, name, cloneRole.trim());
      setCloneOpen(false);
      setEditOpen(false);
      showToast(`${saved.name || name} created from ${draft.name || "agent"} ${sourceLabel}`);
      await loadAll();
      setSelected(saved.name || name);
      await openEditor({ agentId: saved.agentId });
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Could not clone agent");
    } finally {
      setSaving(false);
    }
  }

  const createFromOptions = useMemo(() => {
    const blank = {
      value: "blank",
      label: "Blank agent",
      description: "Default prompts · Guards off · Idle on · 15 min",
      icon: <Sparkles size={16} strokeWidth={1.75} />,
    };
    const clones = catalog.map((a) => ({
      value: a.agentId,
      label: a.name,
      description: agentCapabilitySummary(a),
      avatar: {
        initials: agentInitials(a.name),
        src: a.avatarUrl || undefined,
      },
    }));
    return [blank, ...clones];
  }, [catalog]);

  const left = versions.find((v) => v.version === leftVer) || null;
  const right = versions.find((v) => v.version === rightVer) || null;

  if (!current) {
    return <>
      <PageHeader
        eyebrow="Agent lifecycle"
        title="Agents"
        subtitle="Configure capabilities, validate quality gates, and ship controlled agent releases."
        actions={
          <button type="button" className="btn primary" onClick={openCreateDialog}>
            <Plus />Create agent
          </button>
        }
      />
      <div className="empty-state" style={{ marginTop: 40 }}>
        {loading ? "Loading agents…" : "No agents yet. Create a draft to get started."}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-[#dce5ef]">
          <DialogHeader>
            <DialogTitle>Create a new agent</DialogTitle>
            <DialogDescription>
              Choose Cascade, Direct (GPT-Live mouth), or LiveAvatar (FULL managed avatar), then name the agent.
            </DialogDescription>
          </DialogHeader>
          <div className="modal-form">
            <div className="agent-mode-tiles" role="radiogroup" aria-label="Agent mode">
              <button
                type="button"
                role="radio"
                aria-checked={createMode === "cascade"}
                className={createMode === "cascade" ? "is-active" : undefined}
                onClick={() => setCreateMode("cascade")}
                disabled={saving}
              >
                <b>Cascade</b>
                <span>Separate speech, brain, and voice</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={createMode === "direct"}
                className={createMode === "direct" ? "is-active" : undefined}
                onClick={() => setCreateMode("direct")}
                disabled={saving}
              >
                <b>Direct</b>
                <span>One speech model end to end, Pulse tools</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={createMode === "liveavatar"}
                className={createMode === "liveavatar" ? "is-active" : undefined}
                onClick={() => setCreateMode("liveavatar")}
                disabled={saving}
              >
                <b>LiveAvatar</b>
                <span>LiveAvatar FULL owns listen, think, and speak</span>
              </button>
            </div>
            <label className="agent-identity-field">
              <span>Agent name</span>
              <input
                className="form-control"
                placeholder="Atlas"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                disabled={saving}
                autoFocus
              />
            </label>
            <label className="agent-identity-field">
              <span>Role</span>
              <input
                className="form-control"
                placeholder="Sales Guide"
                value={createRole}
                onChange={(e) => setCreateRole(e.target.value)}
                disabled={saving}
              />
            </label>
            <Select
              className="agent-identity-field"
              label="Start from"
              triggerClassName="form-control"
              value={createFrom}
              onChange={(v) => setCreateFrom(v ?? "blank")}
              searchable={false}
              options={createFromOptions}
            />
          </div>
          <DialogFooter>
            <button type="button" className="btn" disabled={saving} onClick={() => setOpen(false)}>Cancel</button>
            <button type="button" className="btn primary" disabled={saving} onClick={() => void createDraft()}>
              {saving ? "Creating…" : "Create draft"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {toast && <div className="toast"><span className="toast-icon">✓</span><span><b>{toast}</b></span></div>}
    </>;
  }

  return <>
    <PageHeader
      eyebrow="Agent lifecycle"
      title="Agents"
      subtitle="Configure capabilities, validate quality gates, and ship controlled agent releases."
      actions={<>
        <button type="button" className="btn primary" onClick={openCreateDialog}><Plus />Create agent</button>
      </>}
    />
    <div className="metrics">
      <MetricCard
        label="Production agents"
        value={String(productionCount)}
        icon={<Bot />}
        foot={<><span className="delta">{catalog.length} in library</span><span>published</span></>}
      />
      <MetricCard
        label="Published versions"
        value={String(versionSum)}
        icon={<GitBranch />}
        tone="purple"
        foot={<><span className="delta">{current.name} v{current.version || 0}</span><span>selected</span></>}
      />
      <MetricCard
        label="Eval pass rate"
        value={evalPassRate != null ? `${evalPassRate}%` : "—"}
        icon={<CheckCircle2 />}
        tone="green"
        foot={<><span className="delta">{evalTotal} checkup runs</span><span>Checkups</span></>}
      />
      <MetricCard
        label="Unpublished"
        value={String(draftCount)}
        icon={<Code2 />}
        tone="orange"
        foot={<><span>not yet published</span></>}
      />
    </div>
    <div className="agents-layout">
      <Card title="Agent library" kicker="Select an agent to inspect its active release" action={
        <div className="toolbar-group">
          <div className="search-wrap" style={{ minWidth: 175 }}>
            <Search /><input className="field" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find agent" />
          </div>
          <div className="segmented">
            <button aria-label="Grid view" className={view === "grid" ? "active" : ""} onClick={() => setView("grid")}><Grid2X2 size={12} /></button>
            <button aria-label="List view" className={view === "list" ? "active" : ""} onClick={() => setView("list")}><List size={12} /></button>
          </div>
        </div>
      }>
        <div className={view === "grid" ? "agent-grid" : "stack"}>
          {filtered.map((a) => (
            <button key={a.agentId} type="button" className={`agent-tile ${selected === a.name ? "selected" : ""} ${view === "list" ? "list" : ""}`} onClick={() => setSelected(a.name)}>
              <div>
                <div className="agent-top">
                  <div className="agent-identity">
                    <span className="agent-icon" style={a.avatarUrl ? undefined : { background: `linear-gradient(135deg,${a.color},#6654d9)` }}>
                      {a.avatarUrl ? <AgentAvatarImg src={a.avatarUrl} avatarKey={a.avatarKey} /> : <Bot />}
                    </span>
                    <span className="agent-copy">
                      <span className="agent-name">{a.name}</span>
                      <span className="agent-role">{a.role} · v{a.version || 0}</span>
                    </span>
                  </div>
                  <div className="agent-tile-pills">
                    {a.archived ? (
                      <Pill tone="gray">Archived</Pill>
                    ) : settings.defaultAgentId === a.agentId ? (
                      <Pill tone="blue">Default</Pill>
                    ) : null}
                    <Pill
                      tone={
                        normalizeAgentMode(a.agentMode) === "liveavatar"
                          ? "orange"
                          : normalizeAgentMode(a.agentMode) === "direct"
                            ? "purple"
                            : "gray"
                      }
                    >
                      {agentModeLabel(normalizeAgentMode(a.agentMode))}
                    </Pill>
                    {!a.archived ? (
                      <Pill tone={a.published ? "green" : "gray"}>
                        <span className="dot" />
                        {a.published ? "Production" : "Draft"}
                      </Pill>
                    ) : null}
                  </div>
                </div>
                {view === "grid" && <p className="agent-desc">{a.desc}</p>}
              </div>
              <div className="agent-stats">
                <span>
                  <b>{a.sessions30d}</b>
                  <br />
                  sessions / 30d
                </span>
                <span
                  title={
                    a.successPct != null
                      ? `${a.completed30d} of ${a.sessions30d} sessions in the last 30 days ended as Completed / Ended / Recorded (not Failed or abandoned stubs).`
                      : "No sessions in the last 30 days."
                  }
                >
                  <b>{a.successPct != null ? `${a.successPct}%` : "—"}</b>
                  <br />
                  ended OK / 30d
                </span>
              </div>
            </button>
          ))}
        </div>
        {archivedCount > 0 || showArchived ? (
          <button
            type="button"
            className="agent-library-archived-toggle"
            onClick={() => setShowArchived((v) => !v)}
          >
            {showArchived
              ? "Hide archived"
              : `Show archived${archivedCount ? ` (${archivedCount})` : ""}`}
          </button>
        ) : null}
      </Card>
      <div className="stack">
        <Card title="Agent overview" kicker={`${current.name} · ${current.role}`} action={
          <div className="release-card-actions">
            <button
              type="button"
              className="btn"
              disabled={saving}
              onClick={() => void openEditor()}
            >
              Edit
            </button>
            <button
              type="button"
              className="btn primary"
              disabled={saving || current.archived}
              title={current.archived ? "Unarchive before publishing" : undefined}
              onClick={() => setPublishConfirmOpen(true)}
            >
              Publish
            </button>
            <div className="row-action-wrap release-more-wrap" ref={releaseMenuRef}>
              <button
                type="button"
                className="btn icon-only"
                aria-label="More release actions"
                aria-expanded={releaseMenuOpen}
                disabled={saving}
                onClick={() => setReleaseMenuOpen((v) => !v)}
              >
                <MoreVertical size={16} />
              </button>
              {releaseMenuOpen ? (
                <div className="row-menu" role="menu">
                  {!isDefault ? (
                    <button
                      type="button"
                      role="menuitem"
                      disabled={current.archived}
                      onClick={() => void setDefaultAgent()}
                    >
                      <Star size={13} /> Make default
                    </button>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => {
                      setReleaseMenuOpen(false);
                      void openCompare();
                    }}
                  >
                    <GitBranch size={13} /> Open diff
                  </button>
                  {canTestCall ? (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={current.archived}
                    title={current.archived ? "Unarchive before starting a session" : "Open Pulse with this agent"}
                    onClick={() => {
                      setReleaseMenuOpen(false);
                      openAdminTestCall(current.agentId, "working");
                    }}
                  >
                    <Video size={13} /> Test call
                  </button>
                  ) : null}
                  <div className="row-menu-sep" />
                  {current.archived ? (
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => void setAgentArchived(false)}
                    >
                      <ArchiveRestore size={13} /> Unarchive
                    </button>
                  ) : (
                    <button
                      type="button"
                      role="menuitem"
                      className="danger"
                      onClick={() => void setAgentArchived(true)}
                    >
                      <Archive size={13} /> Archive
                    </button>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        }>
          <div className="release-hero">
            <div className="release-hero-top">
              <div className="release-identity">
                <span
                  className={`release-cover${current.avatarUrl ? "" : " is-empty"}`}
                  style={current.avatarUrl ? undefined : { background: `linear-gradient(135deg,${current.color},#6654d9)` }}
                >
                  {current.avatarUrl ? (
                    <AgentAvatarImg src={current.avatarUrl} avatarKey={current.avatarKey} />
                  ) : (
                    <Bot />
                  )}
                </span>
                <div>
                  <span className="release-version">
                    {current.archived ? "Archived agent" : "Current production release"}
                  </span>
                  <h3>{current.name} v{current.version || 0}</h3>
                  <p>
                    {current.archived
                      ? "Not available for new sessions until unarchived"
                      : current.publishedAt
                        ? `Published ${new Date(current.publishedAt).toLocaleString()}`
                        : "Not published yet"}
                  </p>
                </div>
              </div>
              <div className="release-score-wrap" ref={scoreRef}>
                <button
                  type="button"
                  className={`release-score${scoreOpen ? " open" : ""}`}
                  aria-expanded={scoreOpen}
                  aria-haspopup="dialog"
                  aria-label="Ended OK rate — show details"
                  onClick={() => setScoreOpen((v) => !v)}
                >
                  {current.successPct != null ? Math.round(current.successPct) : "—"}
                  <em>%</em>
                </button>
                {scoreOpen ? (
                  <div className="release-score-popover" role="dialog" aria-label="Ended OK rate details">
                    <header>
                      <b>Ended OK / 30d</b>
                      <span>
                        {current.successPct != null ? `${Math.round(current.successPct)}%` : "—"}
                      </span>
                    </header>
                    <p>
                      Share of this agent's sessions in the last 30 days that finished as{" "}
                      <b>Completed</b>, <b>Ended</b>, or <b>Recorded</b>
                      {" "}(not Failed or abandoned stubs).
                    </p>
                    <dl>
                      <div>
                        <dt>Ended OK</dt>
                        <dd>{current.completed30d}</dd>
                      </div>
                      <div>
                        <dt>Sessions</dt>
                        <dd>{current.sessions30d}</dd>
                      </div>
                    </dl>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="release-meta">
              <span>
                Mode
                <b>{agentModeLabel(normalizeAgentMode(current.agentMode))}</b>
              </span>
              <span>
                Voice
                <b>
                  {normalizeAgentMode(current.agentMode) === "liveavatar"
                    ? current.elevenLabsVoiceId
                      ? `ElevenLabs · ${String(current.elevenLabsVoiceId).slice(0, 8)}`
                      : "ElevenLabs"
                    : normalizeAgentMode(current.agentMode) === "direct"
                      ? `GPT-Live · ${current.directVoice || "marin"}`
                      : `${current.voiceName || (current.voiceId ? "Cartesia" : "Default")}${current.ttsEmotion ? ` · ${current.ttsEmotion}` : ""}`}
                </b>
              </span>
              <span>
                Idle
                <b>
                  {normalizeAgentMode(current.agentMode) === "liveavatar"
                    ? "Off (LiveAvatar)"
                    : current.idleEnabled !== false
                      ? `On · ${current.idleSeconds ?? 8}s`
                      : "Off"}
                </b>
              </span>
            </div>
          </div>

          <div className="agent-side-block">
            <div className="mini-label">Capabilities</div>
            <div className="capability-grid">
              <Capability
                icon={<Mic2 size={14} strokeWidth={1.75} />}
                name="Realtime voice"
                note={current.voiceName || (current.voiceId ? "Custom voice" : "Workspace default")}
                onClick={() => void openEditor({ section: "voice" })}
              />
              <Capability
                icon={<Video size={14} strokeWidth={1.75} />}
                name="Video avatar"
                note={
                  current.videoAvatarEnabled
                    ? current.liveAvatarStatus === "ready" && current.liveAvatarId
                      ? `LiveAvatar · ready`
                      : current.liveAvatarStatus === "failed"
                        ? "LiveAvatar · failed"
                        : "LiveAvatar · needs avatar"
                    : "Off · static cover"
                }
                muted={!current.videoAvatarEnabled}
                onClick={() => void openEditor({ section: "videoAvatar" })}
              />
              <Capability
                icon={<Sparkles size={14} strokeWidth={1.75} />}
                name="Perception"
                note="Affect + after-call video"
                onClick={() => void openEditor({ section: "perception" })}
              />
              <Capability
                icon={<Shield size={14} strokeWidth={1.75} />}
                name="Guards"
                note={
                  normalizeAgentMode(current.agentMode) === "direct"
                    ? current.guardrailEnabled
                      ? "On · brain policy"
                      : "Off"
                    : current.guardrailEnabled
                      ? current.guardrailRedirectStyle === "adaptive"
                        ? "On · Adaptive redirect"
                        : "On · Fixed redirect"
                      : "Off"
                }
                muted={!current.guardrailEnabled}
                onClick={() => void openEditor({ section: "guards" })}
              />
              <Capability
                icon={<Timer size={14} strokeWidth={1.75} />}
                name="Idle presence"
                note={
                  current.idleEnabled !== false
                    ? `On · ${current.idleSeconds ?? 8}s silence`
                    : "Off"
                }
                onClick={() => void openEditor({ section: "callControl" })}
              />
              <Capability
                icon={<Clock3 size={14} strokeWidth={1.75} />}
                name="Session limit"
                note={`${current.sessionMaxMinutes} min hard cap`}
                onClick={() => void openEditor({ section: "sessionLimits" })}
              />
            </div>
          </div>
          <div className="agent-side-block">
            <div className="mini-label">Release gates (live)</div>
            <div className="eval-list">
              <Eval
                name="Eval pass rate"
                detail="Checkups passed ÷ (passed + failed)"
                score={evalPassRate != null ? `${evalPassRate}%` : "—"}
                meta={evalPassRate != null ? `${evalTotal} runs` : "No runs yet"}
                ok={evalPassRate == null || evalPassRate >= 80}
              />
              <Eval
                name="Status"
                detail={
                  current.archived
                    ? "Archived agents cannot start new sessions"
                    : "Production release available for new sessions"
                }
                score={
                  current.archived
                    ? "Archived"
                    : current.published
                      ? `v${current.version}`
                      : "Draft"
                }
                meta={
                  current.archived
                    ? "Hidden from default library"
                    : current.published
                      ? "Live"
                      : "Not published"
                }
                ok={!current.archived && current.published}
              />
            </div>
          </div>
        </Card>
      </div>
    </div>

    <Dialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Publish release?</DialogTitle>
          <DialogDescription>
            Creates a new immutable version snapshot of {current.name} and marks it Production.
            New sessions that use this agent will run the published configuration. The working
            draft remains editable; further changes stay unpublished until you publish again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <button type="button" className="btn" disabled={saving} onClick={() => setPublishConfirmOpen(false)}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void publish()}>
            {saving ? "Publishing…" : "Publish release"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-white border-[#dce5ef]">
        <DialogHeader>
          <DialogTitle>Create a new agent</DialogTitle>
          <DialogDescription>
            Choose Cascade, Direct (GPT-Live mouth), or LiveAvatar (FULL managed avatar), then name the agent.
          </DialogDescription>
        </DialogHeader>
        <div className="modal-form">
          <div className="agent-mode-tiles" role="radiogroup" aria-label="Agent mode">
            <button
              type="button"
              role="radio"
              aria-checked={createMode === "cascade"}
              className={createMode === "cascade" ? "is-active" : undefined}
              onClick={() => setCreateMode("cascade")}
              disabled={saving}
            >
              <b>Cascade</b>
              <span>Separate speech, brain, and voice</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={createMode === "direct"}
              className={createMode === "direct" ? "is-active" : undefined}
              onClick={() => setCreateMode("direct")}
              disabled={saving}
            >
              <b>Direct</b>
              <span>One speech model end to end, Pulse tools</span>
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={createMode === "liveavatar"}
              className={createMode === "liveavatar" ? "is-active" : undefined}
              onClick={() => setCreateMode("liveavatar")}
              disabled={saving}
            >
              <b>LiveAvatar</b>
              <span>LiveAvatar FULL owns listen, think, and speak</span>
            </button>
            </div>
          <label className="agent-identity-field">
            <span>Agent name</span>
            <input
              className="form-control"
              placeholder="Atlas"
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </label>
          <label className="agent-identity-field">
            <span>Role</span>
            <input
              className="form-control"
              placeholder="Sales Guide"
              value={createRole}
              onChange={(e) => setCreateRole(e.target.value)}
              disabled={saving}
            />
          </label>
          <Select
            className="agent-identity-field"
            label="Start from"
            triggerClassName="form-control"
            value={createFrom}
            onChange={(v) => {
              const next = v ?? "blank";
              setCreateFrom(next);
              if (next !== "blank" && !createRole.trim()) {
                const src = catalog.find((a) => a.agentId === next);
                if (src?.role) setCreateRole(src.role);
              }
            }}
            searchable={catalog.length > 6}
            searchPlaceholder="Search agents…"
            options={createFromOptions}
          />
        </div>
        <DialogFooter>
          <button type="button" className="btn" disabled={saving} onClick={() => setOpen(false)}>Cancel</button>
          <button type="button" className="btn primary" disabled={saving} onClick={() => void createDraft()}>
            {saving ? "Creating…" : "Create draft"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
      <DialogContent className="agent-compare-dialog flex flex-col bg-white border-[#dce5ef] sm:max-w-[1100px] p-0 gap-0 overflow-hidden">
        <DialogHeader className="agent-config-header">
          <DialogTitle>Compare versions · {current.name}</DialogTitle>
          <DialogDescription>
            Side-by-side published snapshots. Publish a release to create a new version.
          </DialogDescription>
        </DialogHeader>
        {versions.length < 1 ? (
          <div className="empty-state" style={{ padding: 32 }}>No published versions yet. Click Publish release first.</div>
        ) : (
          <>
            <div className="agent-compare-pickers">
              <Select
                label="Left (older)"
                triggerClassName="form-control"
                searchable={versions.length > 8}
                value={leftVer != null ? String(leftVer) : null}
                onChange={(v) => setLeftVer(v != null ? Number(v) : null)}
                options={versions.map((v) => ({
                  value: String(v.version ?? 0),
                  label: (v.version ?? 0) === 0 ? "Current (working)" : `v${v.version}`,
                  description: v.publishedAt ? new Date(v.publishedAt).toLocaleString() : "—",
                }))}
              />
              <Select
                label="Right (newer)"
                triggerClassName="form-control"
                searchable={versions.length > 8}
                value={rightVer != null ? String(rightVer) : null}
                onChange={(v) => setRightVer(v != null ? Number(v) : null)}
                options={versions.map((v) => ({
                  value: String(v.version ?? 0),
                  label: (v.version ?? 0) === 0 ? "Current (working)" : `v${v.version}`,
                  description: v.publishedAt ? new Date(v.publishedAt).toLocaleString() : "—",
                }))}
              />
            </div>
            {versions.filter((v) => (v.version || 0) > 0).length < 2 && (
              <div className="agent-compare-hint">
                Tip: edit a prompt, Save, then Publish again to create v2. Until then, compare <b>Current (working)</b> vs the latest published snapshot.
              </div>
            )}
            <div className="agent-compare-legend">
              <span className="diff-legend del">Removed</span>
              <span className="diff-legend add">Added</span>
              <span className="diff-legend mod">Changed</span>
              <span className="diff-legend eq">Unchanged</span>
            </div>
            <div className="agent-compare-fields">
              <div className="agent-compare-fields-head">
                <span />
                <span>{(left?.version ?? -1) === 0 ? "Current" : `v${left?.version ?? "—"}`}</span>
                <span>{(right?.version ?? -1) === 0 ? "Current" : `v${right?.version ?? "—"}`}</span>
              </div>
              {COMPARE_FIELDS.map((f) => {
                const fmt = (v: unknown) =>
                  v == null
                    ? ""
                    : typeof v === "object"
                      ? JSON.stringify(v, null, 2)
                      : String(v);
                const lv = left ? fmt(left[f.key]) : "";
                const rv = right ? fmt(right[f.key]) : "";
                return (
                  <CompareField key={f.key} label={f.label} left={lv} right={rv} />
                );
              })}
            </div>
          </>
        )}
        <DialogFooter className="agent-config-footer">
          <button type="button" className="btn" onClick={() => setCompareOpen(false)}>Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <AgentEditModal
      open={editOpen}
      onOpenChange={(open) => {
        setEditOpen(open);
        if (!open) setOverwriteOpen(false);
      }}
      draft={draft}
      avatarUrl={current?.avatarUrl}
      avatarKey={current?.avatarKey}
      roleFallback={current?.role}
      published={Boolean(current?.published)}
      archived={Boolean(current?.archived)}
      viewingVersion={viewingVersion}
      versionOptions={editVersionOptions}
      onViewingVersionChange={(v) => void switchViewingVersion(v)}
      section={editSection}
      onSectionChange={setEditSection}
      dirty={editDirty}
      saving={saving}
      onSave={() => void saveDraft()}
      onClone={openCloneFromEditor}
      onTestCall={canTestCall ? () => {
        if (!draft?.agentId) return;
        if (editDirty) {
          showToast("Save configuration before Test call");
          return;
        }
        openAdminTestCall(draft.agentId, viewingVersion);
      } : undefined}
    >
      {draft && editSection === "conversation" ? (
        <AgentEditConversationPanel
          draft={draft}
          onChange={setDraft}
          saving={saving}
          onUploadCover={(file) => void uploadAvatar(draft.agentId, file)}
        />
      ) : draft && editSection === "endOfCall" ? (
        <AgentEditEndOfCallPanel draft={draft} onChange={setDraft} />
      ) : draft && editSection === "guards" ? (
        <AgentEditGuardsPanel
          draft={draft}
          onChange={setDraft}
          variant={normalizeAgentMode(draft.agentMode) === "direct" ? "direct" : "cascade"}
        />
      ) : draft && editSection === "endpointing" && normalizeAgentMode(draft.agentMode) !== "direct" ? (
        <AgentEditEndpointingPanel
          draft={draft}
          onChange={setDraft}
          workspaceVoiceId={settings.voiceId || null}
          workspaceVoiceName={settings.voiceName || null}
        />
      ) : draft && editSection === "callControl" ? (
        <AgentEditCallControlPanel draft={draft} onChange={setDraft} />
      ) : draft && editSection === "sessionLimits" ? (
        <AgentEditSessionLimitsPanel draft={draft} onChange={setDraft} />
      ) : draft && editSection === "voice" ? (
        normalizeAgentMode(draft.agentMode) === "liveavatar" ? (
          <AgentEditLiveAvatarVoicePanel draft={draft} onChange={setDraft} />
        ) : normalizeAgentMode(draft.agentMode) === "direct" ? (
          <AgentEditDirectVoicePanel draft={draft} onChange={setDraft} />
        ) : (
          <AgentEditVoicePanel draft={draft} onChange={setDraft} />
        )
      ) : draft && editSection === "videoAvatar" ? (
        <AgentEditVideoAvatarPanel draft={draft} onChange={setDraft} />
      ) : draft && editSection === "perception" ? (
        <AgentEditPerceptionPanel
          draft={draft}
          onChange={setDraft}
          agentMode={normalizeAgentMode(draft.agentMode)}
        />
      ) : null}
    </AgentEditModal>

    <Dialog open={cloneOpen} onOpenChange={setCloneOpen}>
      <DialogContent className="bg-white border-[#dce5ef] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Clone as new agent</DialogTitle>
          <DialogDescription>
            {viewingVersion === "working"
              ? "Creates a new draft from the configuration currently open (working copy, including unsaved edits)."
              : `Creates a new draft from published v${viewingVersion}.`}
          </DialogDescription>
        </DialogHeader>
        <div className="modal-form">
          <label className="agent-identity-field">
            <span>New agent name</span>
            <input
              className="form-control"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              disabled={saving}
              autoFocus
            />
          </label>
          <label className="agent-identity-field">
            <span>Role</span>
            <input
              className="form-control"
              value={cloneRole}
              onChange={(e) => setCloneRole(e.target.value)}
              disabled={saving}
            />
          </label>
        </div>
        <DialogFooter>
          <button type="button" className="btn" disabled={saving} onClick={() => setCloneOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={saving}
            onClick={() => void confirmCloneFromEditor()}
          >
            {saving ? "Creating…" : "Create draft"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <Dialog open={overwriteOpen} onOpenChange={setOverwriteOpen}>
      <DialogContent className="agent-overwrite-dialog bg-white sm:max-w-md" showCloseButton={false}>
        <DialogHeader>
          <DialogTitle>Overwrite working draft?</DialogTitle>
          <DialogDescription>
            Saving this published version creates a new working draft and replaces the current
            draft on this agent. New sessions will use the overwritten draft until you publish
            again.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:justify-end">
          <button
            type="button"
            className="agent-edit-btn"
            onClick={() => setOverwriteOpen(false)}
          >
            Close without saving
          </button>
          <button
            type="button"
            className="agent-edit-btn agent-edit-btn--primary"
            disabled={saving}
            onClick={() => void confirmOverwriteDraft()}
          >
            Save and overwrite
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {toast && <div className="toast"><span className="toast-icon">✓</span><span><b>{toast}</b></span></div>}
  </>;
}

function Capability({
  icon,
  name,
  note,
  muted,
  onClick,
}: {
  icon: ReactNode;
  name: string;
  note: string;
  muted?: boolean;
  onClick?: () => void;
}) {
  const className = `capability${onClick ? " is-action" : " is-static"}${muted ? " is-muted" : ""}`;
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        <i>{icon}</i>
        <span>
          <b>{name}</b>
          <span>{note}</span>
        </span>
      </button>
    );
  }
  return (
    <div className={className}>
      <i>{icon}</i>
      <span>
        <b>{name}</b>
        <span>{note}</span>
      </span>
    </div>
  );
}

function Eval({
  name,
  detail,
  score,
  meta,
  ok,
}: {
  name: string;
  detail: string;
  score: string;
  meta: string;
  ok: boolean;
}) {
  return (
    <div className="eval-row">
      <div className="eval-row-copy">
        <b>{name}</b>
        <span>{detail}</span>
      </div>
      <div className="eval-row-score">
        <strong>{score}</strong>
        <em>{meta}</em>
      </div>
      <Pill tone={ok ? "green" : "red"}>{ok ? "Passed" : "Needs work"}</Pill>
    </div>
  );
}

function CompareField({ label, left, right }: { label: string; left: string; right: string }) {
  const changed = hasDiff(left, right);
  const rows = diffSideBySide(left, right);
  return (
    <div className={`agent-compare-field ${changed ? "changed" : ""}`}>
      <div className="agent-compare-field-label">
        {label}
        {changed ? <span className="diff-chip">diff</span> : <span className="diff-chip eq">same</span>}
      </div>
      <div className="agent-compare-diff">
        <div className="agent-compare-pane" aria-label="Left version">
          {rows.map((row, i) => (
            <DiffLineView key={`l-${i}`} line={row.left} emptySide="del" />
          ))}
        </div>
        <div className="agent-compare-pane" aria-label="Right version">
          {rows.map((row, i) => (
            <DiffLineView key={`r-${i}`} line={row.right} emptySide="add" />
          ))}
        </div>
      </div>
    </div>
  );
}

function DiffLineView({ line, emptySide }: { line: DiffLine | null; emptySide: "del" | "add" }) {
  if (!line) {
    return <div className={`diff-line empty ${emptySide}`}><span className="diff-gutter">{emptySide === "del" ? "−" : "+"}</span><span className="diff-text"> </span></div>;
  }
  const gutter = line.side === "del" ? "−" : line.side === "add" ? "+" : line.side === "mod" ? "~" : " ";
  return (
    <div className={`diff-line ${line.side}`}>
      <span className="diff-gutter">{gutter}</span>
      <span className="diff-text">
        {line.tokens?.length
          ? line.tokens.map((tok, i) => (
              <span key={i} className={`diff-tok ${tok.t}`}>{tok.text}</span>
            ))
          : (line.text || " ")}
      </span>
    </div>
  );
}
